import React, {useCallback, useEffect, useState} from 'react'
import {Form, Spinner} from 'react-bootstrap'
import toaster from '../../../../Utils/toaster'
import {useAuth} from '../../../modules/auth'
import {getAiAssignmentSetting, updateAiAssignmentSetting} from './_requests'

export function AiAssignmentAgentSettingsForm({customerId}: {customerId: number}) {
  const {currentUser} = useAuth()
  const isSystemAdmin = currentUser?.result?.userType === 'SYSTEM'

  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<'forbidden' | 'failed' | null>(null)
  const [savingAutoAssign, setSavingAutoAssign] = useState(false)
  const [savingMargin, setSavingMargin] = useState(false)

  const [autoAssignEnabled, setAutoAssignEnabled] = useState(false)
  const [minMarginOverride, setMinMarginOverride] = useState<number | null>(null)
  const [marginInput, setMarginInput] = useState('')
  const [defaultMinMarginPercent, setDefaultMinMarginPercent] = useState<number | null>(null)

  const load = useCallback(async () => {
    if (!customerId || customerId <= 0) {
      setLoading(false)
      return
    }

    setLoading(true)
    setLoadError(null)
    try {
      const setting = await getAiAssignmentSetting(customerId)
      if (!setting) {
        setLoadError('failed')
        return
      }

      setAutoAssignEnabled(setting.autoAssignEnabled)
      setMinMarginOverride(setting.minMarginOverride)
      setMarginInput(setting.minMarginOverride === null ? '' : String(setting.minMarginOverride))
      setDefaultMinMarginPercent(setting.defaultMinMarginPercent)
    } catch (err: any) {
      setLoadError(err?.response?.status === 403 ? 'forbidden' : 'failed')
      if (err?.response?.status !== 403) {
        toaster('error', err?.response?.data?.text ?? 'Failed to load AI Assignment Agent settings')
      }
    } finally {
      setLoading(false)
    }
  }, [customerId])

  useEffect(() => {
    load()
  }, [load])

  const settingError = (err: any, fallback: string) =>
    err?.response?.status === 403
      ? 'Only SYSTEM administrators can change this setting'
      : err?.response?.data?.text ?? err?.response?.data?.message ?? fallback

  const toggleAutoAssign = async (enabled: boolean) => {
    if (!isSystemAdmin) return

    setSavingAutoAssign(true)
    try {
      await updateAiAssignmentSetting(customerId, {autoAssignEnabled: enabled, minMarginOverride})
      setAutoAssignEnabled(enabled)
      toaster('success', enabled ? 'Automatic assignment enabled' : 'Automatic assignment disabled')
    } catch (err: any) {
      toaster('error', settingError(err, 'Failed to update automatic assignment'))
    } finally {
      setSavingAutoAssign(false)
    }
  }

  const saveMarginOverride = async () => {
    if (!isSystemAdmin) return

    const trimmed = marginInput.trim()
    let nextOverride: number | null = null

    if (trimmed !== '') {
      const parsed = parseFloat(trimmed)
      if (!Number.isFinite(parsed) || parsed < 0 || parsed > 100) {
        toaster('error', 'Minimum profit margin must be between 0 and 100 percent, or blank')
        return
      }
      nextOverride = parsed
    }

    setSavingMargin(true)
    try {
      await updateAiAssignmentSetting(customerId, {
        autoAssignEnabled,
        minMarginOverride: nextOverride,
      })
      setMinMarginOverride(nextOverride)
      setMarginInput(nextOverride === null ? '' : String(nextOverride))
      toaster(
        'success',
        nextOverride === null
          ? 'This client now uses the global minimum profit margin'
          : `Minimum profit margin set to ${nextOverride}%`
      )
    } catch (err: any) {
      toaster('error', settingError(err, 'Failed to update the minimum profit margin'))
    } finally {
      setSavingMargin(false)
    }
  }

  if (loading) {
    return (
      <div className='d-flex justify-content-center align-items-center py-10'>
        <Spinner animation='border' />
      </div>
    )
  }

  if (loadError) {
    return (
      <div>
        <div className='mb-6'>
          <h4 className='fw-bold text-gray-900 mb-2'>AI Assignment Agent</h4>
        </div>
        <div className='alert alert-light-warning border border-warning'>
          {loadError === 'forbidden'
            ? 'These settings are only visible to SYSTEM administrators.'
            : 'These settings could not be loaded. Refresh the page to try again.'}
        </div>
      </div>
    )
  }

  const marginIsDirty =
    marginInput.trim() !== (minMarginOverride === null ? '' : String(minMarginOverride))

  return (
    <div>
      <div className='mb-6'>
        <h4 className='fw-bold text-gray-900 mb-2'>AI Assignment Agent</h4>
        <p className='text-muted mb-0'>
          The agent contacts qualified interpreters for this client&apos;s new in-person
          appointments regardless of these settings. They control what happens once someone accepts,
          and which interpreters are considered.
        </p>
      </div>

      <div className='card shadow-sm mb-5 border border-gray-200'>
        <div className='card-body p-5'>
          <div className='d-flex flex-column flex-md-row justify-content-between align-items-start gap-4'>
            <div className='flex-grow-1 pe-md-4'>
              <div className='d-flex align-items-center gap-2 mb-2'>
                <h5 className='mb-0 fw-bold text-gray-900'>Assign the interpreter automatically</h5>
              </div>
              <p className='text-muted mb-0 small lh-base' style={{maxWidth: 640}}>
                When enabled, the agent assigns the accepting interpreter with the best profit
                margin as soon as the outreach window closes. When disabled, it highlights that
                interpreter as a recommendation and a coordinator assigns them.
              </p>
              {!isSystemAdmin && (
                <p className='text-muted mb-0 mt-2 small fst-italic'>
                  Only SYSTEM administrators can change this setting.
                </p>
              )}
            </div>
            <div className='d-flex align-items-center gap-2 align-self-md-center'>
              {savingAutoAssign && <Spinner animation='border' size='sm' />}
              <div className='form-check form-switch m-0 ps-0'>
                <Form.Check
                  type='checkbox'
                  role='switch'
                  id='ai-assignment-auto-assign-toggle'
                  className='m-0'
                  checked={autoAssignEnabled}
                  disabled={savingAutoAssign || !isSystemAdmin}
                  onChange={(e) => toggleAutoAssign(e.target.checked)}
                  style={{
                    width: '2.75rem',
                    height: '1.35rem',
                    cursor: savingAutoAssign || !isSystemAdmin ? 'not-allowed' : 'pointer',
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className='card shadow-sm mb-5 border border-gray-200'>
        <div className='card-body p-5'>
          <div className='d-flex flex-column flex-md-row justify-content-between align-items-start gap-4'>
            <div className='flex-grow-1 pe-md-4'>
              <div className='d-flex align-items-center gap-2 mb-2'>
                <h5 className='mb-0 fw-bold text-gray-900'>Minimum profit margin</h5>
              </div>
              <p className='text-muted mb-0 small lh-base' style={{maxWidth: 640}}>
                Interpreters below this margin are not contacted for this client. Leave blank to use
                the global setting
                {defaultMinMarginPercent !== null ? ` of ${defaultMinMarginPercent}%` : ''}.
              </p>
              {!isSystemAdmin && (
                <p className='text-muted mb-0 mt-2 small fst-italic'>
                  Only SYSTEM administrators can change this setting.
                </p>
              )}
            </div>
            <div className='d-flex align-items-center gap-2 align-self-md-center'>
              <div className='input-group' style={{width: 190}}>
                <input
                  type='number'
                  className='form-control'
                  id='ai-assignment-margin-override'
                  value={marginInput}
                  placeholder='Global'
                  min={0}
                  max={100}
                  step={0.01}
                  disabled={savingMargin || !isSystemAdmin}
                  onChange={(e) => setMarginInput(e.target.value)}
                />
                <span className='input-group-text'>%</span>
              </div>
              <button
                type='button'
                className='btn btn-sm btn-primary'
                disabled={savingMargin || !isSystemAdmin || !marginIsDirty}
                onClick={saveMarginOverride}
              >
                {savingMargin ? <Spinner animation='border' size='sm' /> : 'Save'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
